import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Paper,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Typography,
    Card,
    CardHeader,
    CardContent,
    Collapse,
    IconButton,
    Dialog,
    DialogContent,
    DialogActions
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
    Save,
    ExpandMore,
    ExpandLess,
    AddPhotoAlternate,
    Description,
    Delete
} from '@material-ui/icons';
import Swal from 'sweetalert2';
import { EquipmentFormService } from './equipmentFormService';

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    sugestoesList: {
        position: 'absolute',
        zIndex: 10,
        width: '100%',
        maxHeight: 200,
        overflowY: 'auto',
        backgroundColor: 'white',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    sugestaoItem: {
        '&:hover': {
            backgroundColor: '#e3f2fd',
        },
    },
    formButtons: {
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(2),
        borderTop: '1px solid #E9ECEF',
    },
    cancelButton: {
        border: '1px solid #DEE2E6',
        color: '#212529',
        '&:hover': {
            borderColor: '#00A8E8',
            color: '#00A8E8',
        },
    },
    saveButton: {
        backgroundColor: '#00A8E8',
        color: '#FFFFFF',
        '&:hover': {
            backgroundColor: '#0089B5',
        },
    },
    fieldRow: {
        display: 'flex',
        gap: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    fieldHalf: {
        flex: 1,
    },
    sectionCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        marginBottom: theme.spacing(2),
        backgroundColor: '#fff',
        boxShadow: '0 2px 6px rgba(0, 0, 0, .1)',
        overflow: 'hidden',
    },
    sectionHeader: {
        cursor: 'pointer',
        padding: theme.spacing(1, 2),
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:hover': {
            backgroundColor: '#e9ecef',
        },
    },
    sectionBody: {
        padding: theme.spacing(2),
    },
    fotoContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    fotoItem: {
        position: 'relative',
        display: 'inline-block',
    },
    fotoThumb: {
        width: 120,
        height: 120,
        objectFit: 'cover',
        cursor: 'pointer',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
    },
    deleteButton: {
        position: 'absolute',
        top: 0,
        right: 0,
        minWidth: 'auto',
        padding: 4,
        backgroundColor: 'rgba(255,255,255,0.8)',
    },
    laudoItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    disabledSection: {
        pointerEvents: 'none',
        opacity: 0.5,
    },
    disabledMessage: {
        color: '#6c757d',
        fontStyle: 'italic',
        marginBottom: theme.spacing(1),
    },
}));

const EquipmentForm = ({ onSuccess, onCancel, clienteId, equipmentId, currentLanguage }) => {
    const classes = useStyles();
    const sharedTranslations = currentLanguage?.bepiService?.shared || {};
    const resolveMessage = (messageKey) => sharedTranslations?.[messageKey] || currentLanguage?.[messageKey] || messageKey;

    // Estados
    const [servicos, setServicos] = useState([]);
    const [sugestoesServicos, setSugestoesServicos] = useState([]);
    const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingServicos, setLoadingServicos] = useState(true);
    const [equipamentoAtual, setEquipamentoAtual] = useState(null);

    const [sections, setSections] = useState({
        dados: true,
        fotos: true,
        laudos: true
    });

    const [formData, setFormData] = useState({
        servico_id: '',
        servico: '',
        marca: '',
        modelo: '',
        serie: '',
        ca: '',
        obstec: '',
        localizacao: '',
        proxima_revisao: '',
        ultima_revisao: '',
        ativo: true
    });

    const [fotos, setFotos] = useState({ antes: [], depois: [] });
    const [laudos, setLaudos] = useState([]);
    const [modalImagemAberta, setModalImagemAberta] = useState(false);
    const [imagemModalUrl, setImagemModalUrl] = useState('');

    // Carregar dados iniciais
    useEffect(() => {
        carregarServicos();
        if (equipmentId) {
            carregarEquipamento(equipmentId);
        }
    }, [equipmentId]);

    const carregarServicos = async () => {
        setLoadingServicos(true);
        const data = await EquipmentFormService.loadServices();
        setServicos(data);
        setLoadingServicos(false);
    };

    const carregarEquipamento = async (id) => {
        const equip = await EquipmentFormService.loadEquipment(id);
        if (equip) {
            setEquipamentoAtual(equip);
            setFormData({
                servico_id: equip.servico_id,
                servico: equip.servico_nome || '',
                marca: equip.marca || '',
                modelo: equip.modelo || '',
                serie: equip.serie || '',
                ca: equip.ca || '',
                obstec: equip.obstec || '',
                localizacao: equip.localizacao || '',
                proxima_revisao: equip.proxima_revisao || '',
                ultima_revisao: equip.ultima_revisao || '',
                ativo: equip.ativo !== false
            });
            setFotos({
                antes: (equip.fotos || []).filter(f => f.momento === 'antes'),
                depois: (equip.fotos || []).filter(f => f.momento === 'depois')
            });
            setLaudos(equip.laudos || []);
        }
    };

    const toggleSection = (section) => {
        setSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Handlers do Serviço
    const handleServicoChange = (event) => {
        const termo = event.target.value;
        setFormData(prev => ({ ...prev, servico: termo, servico_id: '' }));

        if (!termo) {
            setSugestoesServicos(servicos.slice(0, 10));
        } else {
            const filtrados = servicos.filter(s =>
                s.nome.toLowerCase().includes(termo.toLowerCase())
            ).slice(0, 10);
            setSugestoesServicos(filtrados);
        }
        setMostrarSugestoes(true);
    };

    const selecionarServico = (servico) => {
        setFormData(prev => ({
            ...prev,
            servico_id: servico.id,
            servico: servico.nome
        }));
        setMostrarSugestoes(false);
    };

    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAtivoChange = (event) => {
        const ativo = event.target.checked;
        if (!ativo) {
            Swal.fire(currentLanguage.bepiService.shared.shared_equip_form_attention_label, currentLanguage.bepiService.shared.shared_equip_form_deactivate_warning, 'warning');
        }
        setFormData(prev => ({ ...prev, ativo }));
    };

    // Salvar Equipamento
    const salvarEquipamento = async () => {
        if (!formData.servico_id) {
            Swal.fire(currentLanguage.bepiService.shared.shared_equip_form_error_label, currentLanguage.bepiService.shared.shared_equip_form_select_service_error, 'error');
            return;
        }

        setLoading(true);

        const equipmentData = {
            ...formData,
            user_id: clienteId || null,
            status: formData.ativo ? 'ativo' : 'inativo'
        };

        const id = equipamentoAtual?.id || null;
        const result = await EquipmentFormService.saveEquipment(equipmentData, id);

        setLoading(false);

        if (result.success) {
            Swal.fire({
                icon: 'success',
                title: resolveMessage(result.message),
                timer: 2000,
                showConfirmButton: false
            });

            setEquipamentoAtual({ id: result.data.id });

            if (onSuccess) {
                onSuccess(result.data);
            }
        } else {
            Swal.fire(
                currentLanguage?.bepiService?.shared.shared_details_error_label || 'Erro',
                resolveMessage(result.message) || (currentLanguage?.bepiService?.shared.shared_service_msg_equip_save_error || 'Erro ao salvar equipamento'),
                'error'
            );
        }
    };

    // === FOTOS ===
    const escolherFoto = (momento) => {
        if (!equipamentoAtual) {
            Swal.fire(currentLanguage.bepiService.shared.shared_equip_form_attention_label, currentLanguage.bepiService.shared.shared_equip_form_save_first_warning, 'warning');
            return;
        }

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;

        input.onchange = async (e) => {
            const files = [...e.target.files];

            for (const file of files) {
                const blob = await EquipmentFormService.resizeImage(file, 1000);
                const result = await EquipmentFormService.uploadPhoto(
                    equipamentoAtual.id,
                    momento,
                    blob
                );

                if (result.success) {
                    setFotos(prev => ({
                        ...prev,
                        [momento]: [...prev[momento], result.data]
                    }));
                } else {
                    Swal.fire(currentLanguage.bepiService.shared.shared_details_error_label, result.message, 'error');
                }
            }
        };

        input.click();
    };

    const excluirFoto = async (id, momento) => {
        const confirm = await Swal.fire({
            title: currentLanguage.bepiService.shared.shared_equip_form_remove_photo_title,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: currentLanguage.bepiService.shared.shared_equip_form_yes_btn,
            cancelButtonText: currentLanguage.bepiService.shared.shared_equip_form_no_btn
        });

        if (!confirm.isConfirmed) return;

        const result = await EquipmentFormService.deletePhoto(id);

        if (result.success) {
            setFotos(prev => ({
                ...prev,
                [momento]: prev[momento].filter(f => f.id !== id)
            }));
            Swal.fire(currentLanguage?.bepiService?.shared.shared_details_success_label || 'Sucesso', resolveMessage(result.message), 'success');
        } else {
            Swal.fire(currentLanguage?.bepiService?.shared.shared_details_error_label || 'Erro', resolveMessage(result.message), 'error');
        }
    };

    // === LAUDOS ===
    const prepararLaudo = () => {
        if (!equipamentoAtual) {
            Swal.fire(currentLanguage.bepiService.shared.shared_equip_form_attention_label, currentLanguage.bepiService.shared.shared_equip_form_save_first_warning, 'warning');
            return;
        }

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/pdf';

        input.onchange = async (e) => {
            const file = e.target.files[0];
            const nome = prompt(currentLanguage.bepiService.shared.shared_equip_form_laudo_name_prompt, file.name.replace('.pdf', ''));
            if (!nome) return;

            const result = await EquipmentFormService.uploadLaudo(
                equipamentoAtual.id,
                nome,
                file
            );

            if (result.success) {
                setLaudos(prev => [...prev, result.data]);
            } else {
                Swal.fire(currentLanguage?.bepiService?.shared.shared_details_error_label || 'Erro', result.message, 'error');
            }
        };

        input.click();
    };

    const excluirLaudo = async (id) => {
        const confirm = await Swal.fire({
            title: currentLanguage.bepiService.shared.shared_equip_form_remove_laudo_title,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: currentLanguage.bepiService.shared.shared_equip_form_yes_btn,
            cancelButtonText: currentLanguage.bepiService.shared.shared_equip_form_no_btn
        });

        if (!confirm.isConfirmed) return;

        const result = await EquipmentFormService.deleteLaudo(id);

        if (result.success) {
            setLaudos(prev => prev.filter(l => l.id !== id));
            Swal.fire(currentLanguage?.bepiService?.shared.shared_details_success_label || 'Sucesso', resolveMessage(result.message), 'success');
        } else {
            Swal.fire(currentLanguage?.bepiService?.shared.shared_details_error_label || 'Erro', resolveMessage(result.message), 'error');
        }
    };

    const secoesBloqueadas = !equipamentoAtual;

    return (
        <Box className={classes.formContainer}>
            {/* === SEÇÃO: DADOS DO EQUIPAMENTO === */}
            <Card className={classes.sectionCard}>
                <CardHeader
                    title={currentLanguage.bepiService.shared.shared_equip_form_data_section_title}
                    action={
                        <IconButton onClick={() => toggleSection('dados')}>
                            {sections.dados ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    }
                    className={classes.sectionHeader}
                    onClick={() => toggleSection('dados')}
                />
                <Collapse in={sections.dados}>
                    <CardContent className={classes.sectionBody}>
                        {/* Serviço Vinculado */}
                        <Box position="relative" mb={2}>
                            <TextField
                                fullWidth
                                label={currentLanguage.bepiService.shared.shared_equip_form_service_label}
                                value={formData.servico}
                                onChange={handleServicoChange}
                                onFocus={() => {
                                    setSugestoesServicos(servicos.slice(0, 10));
                                    setMostrarSugestoes(true);
                                }}
                                onBlur={() => setTimeout(() => setMostrarSugestoes(false), 200)}
                                placeholder={loadingServicos ? currentLanguage.bepiService.shared.shared_equip_form_loading_msg : currentLanguage.bepiService.shared.shared_equip_form_search_placeholder}
                                disabled={loadingServicos}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    endAdornment: loadingServicos ? <CircularProgress size={20} /> : null,
                                }}
                            />

                            {mostrarSugestoes && sugestoesServicos.length > 0 && (
                                <Paper className={classes.sugestoesList}>
                                    <List dense>
                                        {sugestoesServicos.map(servico => (
                                            <ListItem
                                                key={servico.id}
                                                button
                                                className={classes.sugestaoItem}
                                                onClick={() => selecionarServico(servico)}
                                            >
                                                <ListItemText primary={servico.nome} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            )}
                        </Box>

                        <Box className={classes.fieldRow}>
                            <TextField className={classes.fieldHalf} name="marca" label={currentLanguage.bepiService.shared.shared_equip_form_brand_label} value={formData.marca} onChange={handleInputChange} variant="outlined" size="small" />
                            <TextField className={classes.fieldHalf} name="modelo" label={currentLanguage.bepiService.shared.shared_equip_form_model_label} value={formData.modelo} onChange={handleInputChange} variant="outlined" size="small" />
                        </Box>

                        <Box className={classes.fieldRow} mt={2}>
                            <TextField className={classes.fieldHalf} name="serie" label={currentLanguage.bepiService.shared.shared_equip_form_serial_label} value={formData.serie} onChange={handleInputChange} variant="outlined" size="small" />
                            <TextField className={classes.fieldHalf} name="ca" label={currentLanguage.bepiService.shared.shared_equip_form_ca_label} value={formData.ca} onChange={handleInputChange} variant="outlined" size="small" />
                        </Box>

                        <Box mt={2}>
                            <TextField fullWidth name="obstec" label={currentLanguage.bepiService.shared.shared_equip_form_obs_label} value={formData.obstec} onChange={handleInputChange} variant="outlined" size="small" multiline rows={2} />
                        </Box>

                        <Box mt={2}>
                            <TextField fullWidth name="localizacao" label={currentLanguage.bepiService.shared.shared_equip_form_location_label} value={formData.localizacao} onChange={handleInputChange} variant="outlined" size="small" />
                        </Box>

                        <Box className={classes.fieldRow} mt={2}>
                            <TextField className={classes.fieldHalf} name="proxima_revisao" label={currentLanguage.bepiService.shared.shared_equip_form_next_rev_label} type="date" value={formData.proxima_revisao} onChange={handleInputChange} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                            <TextField className={classes.fieldHalf} name="ultima_revisao" label={currentLanguage.bepiService.shared.shared_equip_form_last_rev_label} type="date" value={formData.ultima_revisao} onChange={handleInputChange} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
                        </Box>

                        <FormControlLabel
                            control={<Checkbox checked={formData.ativo} onChange={handleAtivoChange} name="ativo" color="primary" />}
                            label={currentLanguage.bepiService.shared.shared_equip_form_active_label}
                            style={{ marginTop: 16 }}
                        />

                        <Box className={classes.formButtons}>
                            <Button variant="outlined" className={classes.cancelButton} onClick={onCancel} disabled={loading}>
                                {currentLanguage.bepiService.shared.shared_equip_form_cancel_btn}
                            </Button>
                            <Button variant="contained" className={classes.saveButton} onClick={salvarEquipamento} disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}>
                                {loading ? currentLanguage.bepiService.shared.shared_equip_form_saving_btn : (equipamentoAtual ? currentLanguage.bepiService.shared.shared_equip_form_save_changes_btn : currentLanguage.bepiService.shared.shared_equip_form_save_equip_btn)}
                            </Button>
                        </Box>
                    </CardContent>
                </Collapse>
            </Card>

            {/* === SEÇÃO: FOTOS === */}
            <Card className={classes.sectionCard}>
                <CardHeader
                    title={currentLanguage.bepiService.shared.shared_equip_form_photos_section_title}
                    action={
                        <IconButton onClick={() => toggleSection('fotos')}>
                            {sections.fotos ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    }
                    className={classes.sectionHeader}
                    onClick={() => toggleSection('fotos')}
                />
                <Collapse in={sections.fotos}>
                    <CardContent className={`${classes.sectionBody} ${secoesBloqueadas ? classes.disabledSection : ''}`}>
                        {secoesBloqueadas && (
                            <Typography className={classes.disabledMessage}>
                                {currentLanguage.bepiService.shared.shared_equip_form_save_first_photos_msg}
                            </Typography>
                        )}

                        <Button variant="contained" color="primary" startIcon={<AddPhotoAlternate />} onClick={() => escolherFoto('antes')} disabled={secoesBloqueadas}>
                            {currentLanguage.bepiService.shared.shared_equip_form_add_photo_before_btn}
                        </Button>
                        <Box className={classes.fotoContainer}>
                            {fotos.antes.map(f => (
                                <Box key={f.id} className={classes.fotoItem}>
                                    <img src={`/api/fotos/${f.nome_arquivo}`} className={classes.fotoThumb} onClick={() => { setImagemModalUrl(`/api/fotos/${f.nome_arquivo}`); setModalImagemAberta(true); }} alt="" />
                                    <IconButton className={classes.deleteButton} size="small" onClick={() => excluirFoto(f.id, 'antes')}>
                                        <Delete />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>

                        <Box mt={2}>
                            <Button variant="contained" color="primary" startIcon={<AddPhotoAlternate />} onClick={() => escolherFoto('depois')} disabled={secoesBloqueadas}>
                                {currentLanguage.bepiService.shared.shared_equip_form_add_photo_after_btn}
                            </Button>
                        </Box>
                        <Box className={classes.fotoContainer}>
                            {fotos.depois.map(f => (
                                <Box key={f.id} className={classes.fotoItem}>
                                    <img src={`/api/fotos/${f.nome_arquivo}`} className={classes.fotoThumb} onClick={() => { setImagemModalUrl(`/api/fotos/${f.nome_arquivo}`); setModalImagemAberta(true); }} alt="" />
                                    <IconButton className={classes.deleteButton} size="small" onClick={() => excluirFoto(f.id, 'depois')}>
                                        <Delete />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Collapse>
            </Card>

            {/* === SEÇÃO: LAUDOS === */}
            <Card className={classes.sectionCard}>
                <CardHeader
                    title={currentLanguage.bepiService.shared.shared_equip_form_laudos_section_title}
                    action={
                        <IconButton onClick={() => toggleSection('laudos')}>
                            {sections.laudos ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    }
                    className={classes.sectionHeader}
                    onClick={() => toggleSection('laudos')}
                />
                <Collapse in={sections.laudos}>
                    <CardContent className={`${classes.sectionBody} ${secoesBloqueadas ? classes.disabledSection : ''}`}>
                        {secoesBloqueadas && (
                            <Typography className={classes.disabledMessage}>
                                {currentLanguage.bepiService.shared.shared_equip_form_save_first_laudos_msg}
                            </Typography>
                        )}

                        <Button variant="contained" color="primary" startIcon={<Description />} onClick={prepararLaudo} disabled={secoesBloqueadas} style={{ marginBottom: 16 }}>
                            {currentLanguage.bepiService.shared.shared_equip_form_send_laudo_btn}
                        </Button>

                        {laudos.map(l => (
                            <Box key={l.id} className={classes.laudoItem}>
                                <Button href={`/api/laudos/${l.nome_arquivo}`} target="_blank">
                                    {l.nome}
                                </Button>
                                <IconButton size="small" onClick={() => excluirLaudo(l.id)}>
                                    <Delete />
                                </IconButton>
                            </Box>
                        ))}
                    </CardContent>
                </Collapse>
            </Card>

            {/* Modal de Imagem */}
            <Dialog open={modalImagemAberta} onClose={() => setModalImagemAberta(false)} maxWidth="lg">
                <DialogContent>
                    <img src={imagemModalUrl} alt="" style={{ width: '100%', height: 'auto' }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalImagemAberta(false)}>{currentLanguage.bepiService.shared.shared_equip_form_close_btn}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

EquipmentForm.propTypes = {
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func,
    clienteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    equipmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

EquipmentForm.defaultProps = {
    onSuccess: null,
    onCancel: null,
    clienteId: null,
    equipmentId: null,
};

export default EquipmentForm;
